import { ReactNode, createContext, useCallback, useMemo, useState } from 'react';
import { InvoiceUploadSignedUrlDto, InvoiceUploadSignedUrlRequestDto } from 'src/generated/services/TFinancialApi';
import { UploadStep } from './types';

export interface IUploadSelectorProps {
  children: ReactNode;
}

export const UploadUIContext = createContext(null);

export const UploadSelector = ({ children }: IUploadSelectorProps) => {
  // UI State Management
  const [isShowUpload, setIsShowUpload] = useState<boolean>(false);
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<UploadStep>(UploadStep.Step1_SelectInvoice);

  // Form State Management
  const [serviceProvider, setServiceProvider] = useState<string>('');
  const [modality, setModality] = useState<InvoiceUploadSignedUrlRequestDto['modality']>(null);
  const [invoiceFiles, setInvoiceFiles] = useState<File[]>([]);

  // Upload Flow State
  const [batchId, setBatchId] = useState<string>('');
  const [uploadResults, setUploadResults] = useState<InvoiceUploadSignedUrlDto[]>([]);
  const [isLoadingUpload, setIsLoadingUpload] = useState<boolean>(false);
  const [errorProcessingStatuses, setErrorProcessingStatuses] = useState<string[]>([]);

  const onSetupUpload = useCallback((provider?: string, modality?: InvoiceUploadSignedUrlRequestDto['modality']) => {
    setIsShowUpload(true);
    if (provider) {
      setServiceProvider(provider);
    }
    if (modality) {
      setModality(modality);
    }
  }, []);

  const onCloseUpload = useCallback(() => {
    setIsShowUpload(false);
    setServiceProvider('');
    setModality(null);
    setInvoiceFiles([]);
  }, [setIsShowUpload]);

  // Reset UI and form state (data processing reset handled by useUploadData)
  const onResetUpload = useCallback(() => {
    setInvoiceFiles([]);
    setServiceProvider('');
    setModality(null);
    setIsMinimized(false);
    setUploadResults([]);
    setBatchId('');
    setActiveStep(UploadStep.Step1_SelectInvoice);
  }, []);

  const value = useMemo(
    () => ({
      // UI State
      isShowUpload,
      isMinimized,
      activeStep,
      setIsMinimized,
      setActiveStep,

      // Form State
      invoiceFiles,
      setInvoiceFiles,
      modality,
      serviceProvider,

      // Upload Flow State
      batchId,
      uploadResults,
      isLoadingUpload,
      setBatchId,
      setUploadResults,
      setIsLoadingUpload,

      // Domain Configuration
      errorProcessingStatuses,
      setErrorProcessingStatuses,

      // Actions
      onCloseUpload,
      onSetupUpload,
      onResetUpload,
    }),
    [
      // UI State
      isShowUpload,
      isMinimized,
      activeStep,

      // Form State
      invoiceFiles,
      modality,
      serviceProvider,

      // Upload Flow State
      batchId,
      uploadResults,
      isLoadingUpload,

      // Domain Configuration
      errorProcessingStatuses,

      // Actions
      onCloseUpload,
      onSetupUpload,
      onResetUpload,
    ]
  );

  return <UploadUIContext.Provider value={value}>{children}</UploadUIContext.Provider>;
};
